'use client';

import { useQuery } from '@tanstack/react-query';
import { getTypesenseClient } from '@util/getTypesense';
import useDebounce from '@util/hooks/useDebounce';
import { normalize } from '@util/index';
import { logError } from '@util/logError';
import { MAKE_ARRAY, type Vehicle } from 'models/shared';
import { SearchResponseHit } from 'typesense/lib/Typesense/Documents';
import { SearchParams } from 'typesense/lib/Typesense/Types';

export type VehicleSearchResult = {
  make: string;
  model: string;
  type: Vehicle['type'];
  hit: SearchResponseHit<Vehicle>;
};

/**
 * Parses a free-text vehicle query (e.g. `2024 Yamaha YZ250F`) into a Typesense
 * `normalized_name` query and an optional year. Logic mirrors
 * `SearchByVehicleForm`'s `searchVehicles`: pull a 4-digit year, then a known
 * make literal (longest match first), and normalize the remainder.
 */
function parseVehicleQuery(raw: string): {
  query: string;
  year: number | null;
} {
  let text = raw.trim();
  let year: number | null = null;

  const yearIndex = text.search(/\d{4}/g);
  if (yearIndex !== -1) {
    const yearMatch = text.match(/\d{4}/g);
    if (yearMatch) {
      const parsed = parseInt(yearMatch[0]);
      if (parsed >= 1950 && parsed < 2100) {
        year = parsed;
        text =
          yearIndex === 0
            ? text.substring(4).trim()
            : text.replace(/\d{4}/, '').trim();
      }
    }
  }

  let extractedMake: string | null = null;
  const searchLower = text.toLowerCase();
  for (const make of [...MAKE_ARRAY].sort((a, b) => b.length - a.length)) {
    const idx = searchLower.indexOf(make.toLowerCase());
    if (idx !== -1) {
      extractedMake = make;
      text = (text.slice(0, idx) + text.slice(idx + make.length))
        .replace(/\s+/g, ' ')
        .trim();
      break;
    }
  }

  const normalizedRest = normalize(text);
  const query = extractedMake
    ? `${normalize(extractedMake)}-${normalizedRest}`.trim()
    : normalizedRest;

  return { query, year };
}

async function searchVehicles(raw: string): Promise<VehicleSearchResult[]> {
  const { query, year } = parseVehicleQuery(raw);

  const searchParams: SearchParams<Vehicle, 'off'> = {
    q: query,
    query_by: 'normalized_name',
    per_page: 25,
    exhaustive_search: true,
    sort_by: '_text_match:desc,popularity:desc',
  };
  if (year) searchParams.filter_by = `years:${year}`;

  const res = await getTypesenseClient<Vehicle>('vehicles_alias')
    .documents()
    .search(searchParams);

  // Dedupe hits down to one row per make/model/type.
  const unique = new Map<string, VehicleSearchResult>();
  res.hits?.forEach((hit) => {
    const vehicle = hit.document;
    const key = `${vehicle.make}-${vehicle.model}-${vehicle.type}`;
    if (!unique.has(key)) {
      unique.set(key, {
        make: vehicle.make,
        model: vehicle.model,
        type: vehicle.type,
        hit,
      });
    }
  });

  return Array.from(unique.values());
}

/**
 * Debounced vehicle search against the Typesense `vehicles_alias` collection.
 * Pass the raw input value; the hook handles debouncing and request state.
 */
export function useVehicleSearch(query: string) {
  const debouncedQuery = useDebounce(query, 500);
  const trimmedQuery = debouncedQuery.trim();

  const { data, isFetching } = useQuery({
    queryKey: ['create-listing-vehicle-search', trimmedQuery],
    queryFn: async () => {
      try {
        return await searchVehicles(trimmedQuery);
      } catch (error) {
        logError(error, 'Error searching vehicles:');
        return [] as VehicleSearchResult[];
      }
    },
    enabled: !!trimmedQuery,
  });

  return {
    results: data ?? [],
    isLoading: isFetching && !!trimmedQuery,
    extractedYear: trimmedQuery ? parseVehicleQuery(trimmedQuery).year : null,
    hasQuery: !!trimmedQuery,
  };
}
